// Изборът на листа „Инвентаризация" (изнесен от страницата): търсачката,
// „Целият списък" и „Нов продукт" водят фокуса до реда.
//  • продукт от търсачката → СКОК до „Намерено" (редът се разкрива при всеки
//    филтър — без нов ред, целият каталог е в таблицата);
//  • рецепта от търсачката или от списъка → „Приготвени рецепти" (без
//    повторение — вече добавената само се фокусира) с фокус в „Порции";
//  • продукти от списъка → закачени в „За броене" в реда на избора, фокусът —
//    на първия избран (продукт или рецепта); въведеното в реда „Намерено" се
//    вписва (по мерната единица, изразът „10+5" се смята) и редът минава в
//    „Преброени", а порциите — в рецептата;
//  • „Нов продукт" → закачен най-отгоре, фокусът в „Намерено" чак СЛЕД
//    затварянето на модала (иначе модалът го взима).
import { useRef, type Dispatch, type SetStateAction } from "react";
import type { Lookup } from "../../../../api/inventory";
import type { GridRowKey } from "../../components/gridNavigation";
import type { CatalogEntryValues } from "../../components/search/CatalogPickerModal";
import type { StocktakeSearchRow } from "./StocktakeWorksheetSearch";
import {
  STOCKTAKE_COLS,
  emptyStocktakeRow,
  formatQtyInput,
  parseQtyExpression,
  type StocktakeRowDraft,
} from "./stocktakeWorksheetShared";

type Params = {
  setSearch: (value: string) => void;
  focusSearch: () => void;
  focusCell: (rowKey: GridRowKey, col: number) => void;
  setRevealedProductIds: Dispatch<SetStateAction<Set<number>>>;
  setPinnedProductIds: Dispatch<SetStateAction<number[]>>;
  recipes: Lookup[];
  // добавя рецептите (с въведените порции по id на рецепта) и връща ключа на
  // реда на първата (`r12`)
  addRecipes: (recipes: Lookup[], portionsById?: ReadonlyMap<number, string>) => string | null;
  // „Намерено" от „Целия списък": редовете на листа и зареждането им наведнъж
  // (то нагласява и блока „Преброени", но не вдига черновата — затова `touch`)
  // + знаците по мерната единица
  counted: {
    rows: Record<number, StocktakeRowDraft>;
    replaceAllRows: (rows: Record<number, StocktakeRowDraft>) => void;
    decimalsOf: (productId: number) => number;
    touch: () => void;
  };
};

const COUNTED_COL = STOCKTAKE_COLS.counted;

export const useStocktakePicking = ({
  setSearch,
  focusSearch,
  focusCell,
  setRevealedProductIds,
  setPinnedProductIds,
  recipes,
  addRecipes,
  counted,
}: Params) => {
  // Скрит от филтър ред се разкрива (важи за ВСИЧКИ филтри на изгледа)
  const reveal = (productId: number) =>
    setRevealedProductIds((ids) => (ids.has(productId) ? ids : new Set(ids).add(productId)));

  const focusRecipe = (rowKey: string | null) => {
    if (rowKey) focusCell(rowKey, COUNTED_COL);
  };

  const pickFromSearch = (row: StocktakeSearchRow) => {
    setSearch("");
    if (row.kind === "recipe") {
      focusRecipe(addRecipes([{ id: row.recipeId, name: row.name }]));
      return;
    }
    reveal(row.product.id);
    focusCell(row.product.id, COUNTED_COL);
  };

  // „Целият списък": продуктите с id, рецептите с −id, в реда на избора.
  // Закачените се и РАЗКРИВАТ: избран чип за статус (напр. „Преброени") иначе
  // би скрил блока „За броене" и фокусът нямаше къде да отиде.
  const pickFromCatalog = (catalogIds: number[], values?: CatalogEntryValues) => {
    if (!catalogIds.length) return;
    const productIds = catalogIds.filter((id) => id > 0);
    const recipeById = new Map(recipes.map((recipe) => [recipe.id, recipe]));
    const pickedRecipes = catalogIds
      .filter((id) => id < 0)
      .map((id) => recipeById.get(-id))
      .filter((recipe): recipe is Lookup => !!recipe);
    const portionsById = new Map(
      pickedRecipes.flatMap((recipe) => {
        const typed = values?.get(-recipe.id)?.qty ?? "";
        return typed === "" ? [] : [[recipe.id, typed] as const];
      }),
    );
    const firstRecipeKey = addRecipes(pickedRecipes, portionsById);
    // „Намерено": като при напускане на клетката — по мерната единица, изразът
    // се смята; стойността на разликата се извежда наново
    const countedUpdates = productIds.flatMap((productId) => {
      const typed = values?.get(productId)?.qty ?? "";
      if (typed === "") return [];
      const row = counted.rows[productId] ?? emptyStocktakeRow();
      const value = formatQtyInput(parseQtyExpression(typed), counted.decimalsOf(productId));
      return [[productId, { ...row, counted: value, net: "", gross: "" }] as const];
    });
    if (countedUpdates.length) {
      counted.replaceAllRows({ ...counted.rows, ...Object.fromEntries(countedUpdates) });
      counted.touch();
    }
    if (productIds.length) {
      setPinnedProductIds((ids) => [...ids, ...productIds.filter((id) => !ids.includes(id))]);
      setRevealedProductIds((ids) => new Set([...ids, ...productIds]));
    }
    if (catalogIds[0] < 0) focusRecipe(firstRecipeKey);
    else focusCell(catalogIds[0], COUNTED_COL);
  };

  const createdProductIdRef = useRef<number | null>(null);
  const onProductCreated = (productId: number) => {
    createdProductIdRef.current = productId;
    setSearch("");
    reveal(productId);
    setPinnedProductIds((ids) => (ids.includes(productId) ? ids : [...ids, productId]));
  };
  const onProductModalClosed = (created: boolean) => {
    const productId = createdProductIdRef.current;
    createdProductIdRef.current = null;
    if (created && productId != null) focusCell(productId, COUNTED_COL);
    else focusSearch();
  };

  return { reveal, pickFromSearch, pickFromCatalog, onProductCreated, onProductModalClosed };
};
